/**
 * useAgentSession — Agent session state management
 * Maintains Context_Window in browser memory with FIFO eviction.
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { useLocation } from '@tanstack/react-router'
import type { AgentMessage, SessionContext, InteractionMode } from '@/lib/api/agent'

const MAX_MESSAGES = 50
const MAX_KNOWLEDGE_CHUNKS = 10
const MAX_ACTIVE_ENTITIES = 5
const IDLE_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

interface EntityRef { entity_type: string; entity_id: string; display_text?: string }
interface KnowledgeRef { chunk_id: string; domain: string; content: string; relevance_score?: number }

interface AgentSessionState {
  sessionId: string
  messages: AgentMessage[]
  knowledgeChunks: KnowledgeRef[]
  activeEntities: EntityRef[]
  mode: InteractionMode
  isExpired: boolean
  lastActivity: number
}

function createSession(): AgentSessionState {
  return {
    sessionId: crypto.randomUUID(),
    messages: [],
    knowledgeChunks: [],
    activeEntities: [],
    mode: 'text',
    isExpired: false,
    lastActivity: Date.now(),
  }
}

export function useAgentSession(userRole: string, companyId: string) {
  const [state, setState] = useState<AgentSessionState>(createSession)
  const location = useLocation()
  const idleTimer = useRef<ReturnType<typeof setTimeout>>()

  // Reset idle timer on activity
  const touch = useCallback(() => {
    setState(s => ({ ...s, lastActivity: Date.now(), isExpired: false }))
    if (idleTimer.current) clearTimeout(idleTimer.current)
    idleTimer.current = setTimeout(() => {
      setState(s => ({ ...s, isExpired: true }))
    }, IDLE_TIMEOUT_MS)
  }, [])

  // Auto-detect page entity from URL
  useEffect(() => {
    const parts = location.pathname.split('/').filter(Boolean)
    const entityMap: Record<string, string> = {
      'service-cases': 'service_case', cases: 'service_case', jobs: 'job',
      customers: 'customer', invoices: 'invoice', estimates: 'estimate',
      appointments: 'appointment', assets: 'asset',
    }
    if (parts.length >= 2) {
      const type = entityMap[parts[0]]
      const id = parts[1]
      if (type && id && /^[0-9a-f-]{36}$/i.test(id)) {
        addEntity({ entity_type: type, entity_id: id })
      }
    }
  }, [location.pathname])

  const addMessage = useCallback((msg: AgentMessage) => {
    touch()
    setState(s => {
      if (s.isExpired) {
        const fresh = createSession()
        return { ...fresh, messages: [msg] }
      }
      const messages = [...s.messages, msg]
      if (messages.length > MAX_MESSAGES) messages.shift()
      return { ...s, messages }
    })
  }, [touch])

  const addKnowledgeChunk = useCallback((chunk: KnowledgeRef) => {
    setState(s => {
      const chunks = [...s.knowledgeChunks, chunk]
      if (chunks.length > MAX_KNOWLEDGE_CHUNKS) chunks.shift()
      return { ...s, knowledgeChunks: chunks }
    })
  }, [])

  const addEntity = useCallback((entity: EntityRef) => {
    setState(s => {
      const exists = s.activeEntities.some(e => e.entity_type === entity.entity_type && e.entity_id === entity.entity_id)
      if (exists) return s
      const entities = [...s.activeEntities, entity]
      if (entities.length > MAX_ACTIVE_ENTITIES) entities.shift()
      return { ...s, activeEntities: entities }
    })
  }, [])

  const setMode = useCallback((mode: InteractionMode) => {
    touch()
    setState(s => ({ ...s, mode }))
  }, [touch])

  const getContext = useCallback((): SessionContext => ({
    session_id: state.sessionId,
    messages: state.messages,
    knowledge_chunks: state.knowledgeChunks,
    active_entities: state.activeEntities,
    page_context: { path: location.pathname },
    user_role: userRole,
    company_id: companyId,
  }), [state, location.pathname, userRole, companyId])

  return {
    sessionId: state.sessionId,
    messages: state.messages,
    mode: state.mode,
    isExpired: state.isExpired,
    addMessage,
    addKnowledgeChunk,
    addEntity,
    setMode,
    getContext,
    touch,
  }
}
